import {useInviteContextQuery} from "@src/features/auth/redux/auth-api";
import {useAddRideMutation} from "../redux/rides-api";
import {useState, type FormEvent} from "react";
import {Link, useNavigate, useParams} from "react-router-dom";
import {ArrowLeft, ArrowRight} from "lucide-react";
import {LocationField, type RidePlace} from "./location-field";
import {RideShell, RideLoading, RideNotice, SignInPrompt} from "./ride-shell";
import {useRideLanguage} from "../ride-language";
import {apiMessage, apiStatus, rideDay} from "../ride-utils";

function localDateTime(date: Date) {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
}

export function AddRideScreen() {
  const {t} = useRideLanguage();
  const {spaceId = ""} = useParams();
  const navigate = useNavigate();
  const base = `/quick-actions/${encodeURIComponent(spaceId)}/rides`;
  const login = `/login/${encodeURIComponent(spaceId)}?next=add`;
  const context = useInviteContextQuery(spaceId, {refetchOnMountOrArgChange: 30});
  const member = context.data?.session?.spacesPublicInfo.some(space => space.spaceId === spaceId);
  const [addRide, addState] = useAddRideMutation();
  const [driving, setDriving] = useState(true);
  const [departure, setDeparture] = useState(() => localDateTime(new Date(Date.now() + 3600000)));
  const [fromPlace, setFromPlace] = useState<RidePlace>();
  const [toPlace, setToPlace] = useState<RidePlace>();
  const [room, setRoom] = useState(3);
  const [notes, setNotes] = useState("");
  const [flexMinutes, setFlexMinutes] = useState(0);
  const [error, setError] = useState("");
  const [expired, setExpired] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const submit = async (event: FormEvent) => {
    event.preventDefault(); setError(""); setAttempted(true);
    if (addState.isLoading) return;
    if (!fromPlace || !toPlace) { setError("Choose both addresses from the location suggestions."); return; }
    const date = new Date(departure);
    if (!Number.isFinite(date.getTime()) || date.getTime() < Date.now() + 300000) {
      setError("Choose a departure time at least five minutes from now."); return;
    }
    try {
      const result = await addRide({
        spaceId, driving, departureTime: date.toISOString(), fromName: fromPlace.name, fromLat: fromPlace.lat,
        fromLng: fromPlace.lng, toName: toPlace.name, toLat: toPlace.lat, toLng: toPlace.lng,
        room: driving ? room : 1, notes: notes.trim(), flexMinutes,
      }).unwrap();
      const query = new URLSearchParams({date: rideDay(date), added: driving ? "ride" : "request"});
      if (driving) query.set("ride", result.rideId);
      navigate(`${base}?${query}`, {replace: true});
    } catch (failure) {
      setExpired(apiStatus(failure) === 401);
      setError(apiMessage(failure, "We couldn’t add your ride. Check the details and try again."));
    }
  };
  return <RideShell action={<Link className="ride-link" to={base}><ArrowLeft size={16}/> {t("All rides")}</Link>}>
    <div className="ride-add">
      {context.isLoading ? <RideLoading/> : context.error ? <RideNotice error>{t(apiMessage(context.error, "We couldn’t check your account."))} <button className="ride-link" onClick={() => void context.refetch()}>{t("Try again")}</button></RideNotice>
        : !member ? <SignInPrompt href={login} text={t("Sign in to your group before sharing a ride.")}/>
        : <form className="ride-panel" onSubmit={submit}>
          <header style={{marginBottom: 26}}><h1>{t("Add ride")}</h1></header>
          <div className="ride-segment" style={{marginBottom: 24}}>
            <button type="button" aria-pressed={driving} onClick={() => setDriving(true)}>{t("I’m driving")}</button>
            <button type="button" aria-pressed={!driving} onClick={() => setDriving(false)}>{t("I need a ride")}</button>
          </div>
          {!driving && <RideNotice>{t("To join an existing ride,")} <Link className="ride-link" to={base}>{t("browse available rides")}</Link>.</RideNotice>}
          <LocationField required label={t("Pickup location")} value={fromPlace} onChange={setFromPlace} error={attempted && !fromPlace ? t("Choose a pickup address from the suggestions.") : undefined}/>
          <LocationField required label={t("Destination")} value={toPlace} onChange={setToPlace} error={attempted && !toPlace ? t("Choose a destination from the suggestions.") : undefined}/>
          <label className="ride-field">{t("Departure")}<input className="ride-input" type="datetime-local" dir="ltr" required value={departure}
            min={localDateTime(new Date(Date.now() + 300000))} max={localDateTime(new Date(Date.now() + 365 * 86400000))}
            onChange={event => setDeparture(event.target.value)}/><span className="ride-help">{t("Local time: {zone}", {zone: Intl.DateTimeFormat().resolvedOptions().timeZone})}</span></label>
          <div className="ride-form-grid">
            {driving && <label className="ride-field">{t("Available seats")}<select className="ride-input" value={room} onChange={event => setRoom(Number(event.target.value))}>{[1,2,3,4,5,6].map(value => <option key={value} value={value}>{t(value === 1 ? "{count} seat" : "{count} seats", {count: value})}</option>)}</select></label>}
            <label className="ride-field">{t("Time flexibility")}<select className="ride-input" value={flexMinutes} onChange={event => setFlexMinutes(Number(event.target.value))}>{[0,15,30,60,90,120,180].map(value => <option key={value} value={value}>{value === 0 ? t("Exact time") : t("± {minutes} min", {minutes: value})}</option>)}</select></label>
          </div>
          <label className="ride-field">{t("Notes")} <span className="ride-help">{t("(optional)")}</span><textarea className="ride-input" rows={3} maxLength={255} value={notes} placeholder={t("Pickup details or luggage space")} onChange={event => setNotes(event.target.value)}/></label>
          {error && <RideNotice error>{t(error)}{expired && <Link className="ride-link" to={login}>{t("Sign in again")}</Link>}</RideNotice>}
          <div className="ride-card-actions"><Link className="ride-button ride-button-secondary" to={base}>{t("Cancel")}</Link><button className="ride-button" type="submit" disabled={addState.isLoading}>{addState.isLoading ? t("Publishing…") : driving ? t("Publish ride") : t("Post ride request")} <ArrowRight size={17}/></button></div>
        </form>}
    </div>
  </RideShell>;
}
